import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { Link } from 'site-modules/shared/components/link/link';
import { buildPressReleasesPath, PressModel } from 'client/data/models/press';

import './press-releases-archive.scss';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const CURRENT_YEAR = new Date().getFullYear();
const RECENT_YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
const RECENT_YEARS_SET = new Set(RECENT_YEARS);

const YEAR_TABS = RECENT_YEARS.map(year => ({ id: String(year), label: String(year) }));
const ALL_TABS = [...YEAR_TABS];
const ARCHIVE_LINK_HREF = '/press/press-archive.html';

function parsePublishedDate(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  return {
    year: d.getUTCFullYear(),
    monthIndex: d.getUTCMonth(),
    formatted: `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`,
  };
}

function groupReleasesByTab(releases) {
  const groups = {};
  RECENT_YEARS.forEach(year => {
    groups[String(year)] = [];
  });
  groups.archive = [];

  releases.forEach(release => {
    const parsed = parsePublishedDate(release.published);
    if (parsed && RECENT_YEARS_SET.has(parsed.year)) {
      groups[String(parsed.year)].push(release);
    } else {
      groups.archive.push(release);
    }
  });

  return groups;
}

const ReleasePropType = PropTypes.shape({
  title: PropTypes.string,
  location: PropTypes.string,
  published: PropTypes.string,
});

function ArchiveCard({ release }) {
  const { title, location: rawLocation, published } = release;
  const titleText = typeof title === 'string' ? title : '';
  const location = rawLocation && !rawLocation.endsWith('.html') ? `${rawLocation}.html` : rawLocation;
  const parsed = parsePublishedDate(published);
  const monthLabel = parsed ? MONTH_NAMES[parsed.monthIndex] : /* istanbul ignore next */ null;
  const formattedDate = parsed ? parsed.formatted : /* istanbul ignore next */ null;

  return (
    <Col xs={12} sm={6} lg={3} className="mb-1_5">
      <div className="press-releases-archive__card h-100 p-0_75">
        {!!monthLabel && <p className="size-16 text-blue-50 mb-0_5">{monthLabel}</p>}
        <h3 className="size-16 fw-bold text-gray-darker mb-0_5">
          {location ? (
            <Link to={location} className="text-gray-darker">
              {titleText}
            </Link>
          ) : (
            titleText
          )}
        </h3>
        {!!formattedDate && <p className="size-16 text-gray-darker mb-0">Published: {formattedDate}</p>}
      </div>
    </Col>
  );
}

ArchiveCard.propTypes = {
  release: ReleasePropType.isRequired,
};

export function PressReleasesArchiveUI({ entry = DEFAULT_CONTENT, pressReleases = null }) {
  const [activeTab, setActiveTab] = useState(ALL_TABS[0].id);
  const onTabClick = useCallback(e => setActiveTab(e.currentTarget.dataset.tabId), []);
  const isPressReleasesLoading = pressReleases == null;
  const grouped = useMemo(
    () => groupReleasesByTab(isPressReleasesLoading ? [] : pressReleases),
    [isPressReleasesLoading, pressReleases]
  );

  if (!entry.title) {
    return null;
  }

  const activeReleases = grouped[activeTab] || /* istanbul ignore next */ [];
  let tabPanelContent;
  if (isPressReleasesLoading) {
    tabPanelContent = (
      <p className="size-16 text-gray-dark" role="status">
        Loading&hellip;
      </p>
    );
  } else if (activeReleases.length) {
    tabPanelContent = (
      <Row>
        {activeReleases.map(release => (
          <ArchiveCard key={release.location || release.title} release={release} />
        ))}
      </Row>
    );
  } else {
    tabPanelContent = <p className="size-16 text-gray-dark">No press releases found.</p>;
  }

  return (
    <section className="press-releases-archive py-2" aria-label="Press releases archive">
      <div
        className="press-releases-archive__tabs d-flex mb-1_5"
        role="tablist"
        aria-label="Press releases archive year tabs"
      >
        {ALL_TABS.map(({ id, label }) => (
          <button
            key={id}
            role="tab"
            aria-selected={activeTab === id}
            aria-controls={`archive-panel-${id}`}
            data-tab-id={id}
            className={`press-releases-archive__tab btn btn-link size-16 px-1 py-0_5 text-decoration-none ${activeTab === id ? 'press-releases-archive__tab--active' : ''}`}
            onClick={onTabClick}
          >
            {label}
          </button>
        ))}
        <Link
          to={ARCHIVE_LINK_HREF}
          className="press-releases-archive__tab btn btn-link size-16 px-1 py-0_5 text-decoration-none"
        >
          Archive
        </Link>
      </div>
      <div id={`archive-panel-${activeTab}`} role="tabpanel">
        {tabPanelContent}
      </div>
    </section>
  );
}

PressReleasesArchiveUI.propTypes = {
  entry: CmsEntities.Content,
  pressReleases: PropTypes.arrayOf(ReleasePropType),
};

export const PressReleasesArchive = connectToModel(PressReleasesArchiveUI, {
  pressReleases: bindToPath(buildPressReleasesPath(), PressModel),
});
